/**
 * GENERATED FROM contract/ — DO NOT EDIT.
 *
 * Regenerate with `npm --prefix web run api:generate`; `npm --prefix web run api:check` fails if
 * this file and the contract disagree.
 */

import { http, withQuery, type RequestOptions } from '@/api/http'

import type {
  AccountResult,
  AccountWorkspace,
  AddObligationBody,
  AnalyzeClientBody,
  AnalyzeResult,
  AudienceFacesBody,
  AudienceFacesResult,
  AutosendState,
  AutosendStateQuery,
  AutosendStopAll,
  BackfillDatesBody,
  CancelRunJobsBody,
  ChatReplyResult,
  CloseObligationBody,
  CompanyPeers,
  CompanyPeersQuery,
  ConfirmChatReplyBody,
  ConfirmedPayments,
  CoreGrowResult,
  CreatePaymentBody,
  CreateShareBody,
  CreditsResult,
  CrmContactsResult,
  DatesResult,
  DemoRequestList,
  DemoRequestResult,
  DeriveTermsBody,
  DevTierBody,
  DraftRunBody,
  DraftRunResult,
  DrainResult,
  EmailVerifyResult,
  EnrichResult,
  EnrichRunBody,
  EstimateResult,
  ExtendRunBody,
  GeoVocab,
  GradeObjectsBody,
  GradeObjectsResult,
  GrantTierBody,
  GrantTierResult,
  GrowCoreBody,
  HealthResult,
  ImportLocalBody,
  ImportResult,
  IndustryVocab,
  LedgerSaveResult,
  LifecycleSendList,
  ListContactsBody,
  ListDemoRequestsQuery,
  MarkSentBody,
  MeResult,
  Obligation,
  PayCreateResult,
  PayReturnedBody,
  PhrasesReachBody,
  PhrasesReachResult,
  PlanAddBody,
  PlanCadenceBody,
  PlanDraftBody,
  PlanDraftResult,
  PlanRemoveBody,
  PlanResult,
  PlanRunBody,
  PlanRunResult,
  PlanSearchBody,
  PlanTextBody,
  PrefilterResult,
  PrefilterRunBody,
  ProposeObjectsBody,
  ProposeObjectsResult,
  ProposeTargetingBody,
  QueueScoringBody,
  RecoverSearchBody,
  ReplyResult,
  RequestDemoBody,
  ResolveDomainsBody,
  ResolveResult,
  ResumeQuery,
  ResumeResult,
  RunCruxSearchBody,
  RunEstimateQuery,
  RunGeoResult,
  RunJobsQuery,
  RunJobsResult,
  RunListItem,
  RunPoolResult,
  RunRowsQuery,
  RunRowsResult,
  RunSearchBody,
  SaveLedgerBody,
  SavePicksBody,
  SaveReplyBody,
  SaveScoresBody,
  SaveSearchBody,
  SaveStateBody,
  SavedResult,
  ScoreRunBody,
  ScoreRunResult,
  SearchRecoverResult,
  SearchResult,
  SearchSaveResult,
  SentResult,
  ShareLink,
  ShareView,
  SignupReport,
  SignupsQuery,
  StartAutosendBody,
  StateSaveResult,
  StopAutosendAllBody,
  StopAutosendBody,
  SubscribeBody,
  SubscribeResult,
  SupportChatBody,
  SupportChatResult,
  SupportHandoffBody,
  SupportHandoffResult,
  SuppressionBody,
  TargetingProposal,
  TermsResult,
  TickBody,
  TickResult,
  TitleVocab,
  ValueReachBody,
  ValueReachResult,
  VerifyBody,
} from '@/api/generated/types'

/**
 * The typed client, grouped by the first path segment the way the monolith's generated client is:
 * `apiClient.runs.draftRun(id, body)`.
 *
 * Transport is the hand-written `http` wrapper — it owns the timeout, the JSON parsing and the
 * `RequestError`, none of which belong in generated code.
 */
export const apiClient = {
  meta: {
    /**
     * Mode, which keys are present, the lanes and the DB counts. Safe on every page load.
     *
     * Costs: free.
     */
    health: (options?: RequestOptions) =>
      http.get<HealthResult>(`/api/health`, options),
    /**
     * Row counts across the database.
     *
     * Costs: free.
     */
    stats: (options?: RequestOptions) =>
      http.get<Record<string, number>>(`/api/stats`, options),
    /**
     * The enrichment credit balance. Meaningless while the research provider is default, and says so.
     *
     * Costs: free.
     */
    credits: (options?: RequestOptions) =>
      http.get<CreditsResult>(`/api/credits`, options),
  },
  vocab: {
    /**
     * The interest tags the profile index actually answers to, with their measured reach.
     *
     * Costs: free.
     */
    industryVocab: (options?: RequestOptions) =>
      http.get<IndustryVocab>(`/api/vocab/industries`, options),
    /**
     * The job titles members actually write, with the reach a match_phrase filter gets.
     *
     * Costs: free.
     */
    titleVocab: (options?: RequestOptions) =>
      http.get<TitleVocab>(`/api/vocab/titles`, options),
    /**
     * Verified country ids with live reach.
     *
     * Costs: free.
     */
    geoVocab: (options?: RequestOptions) =>
      http.get<GeoVocab>(`/api/vocab/geo`, options),
    /**
     * How many people a typed picker value reaches on its own, counted with the very clause the run will send. Free: one size:0 count, no quota, no credits, no model. POST rather than GET so a person’s typing does not land in a proxy access log as a URL.
     *
     * Costs: free.
     */
    valueReach: (body: ValueReachBody, options?: RequestOptions) =>
      http.post<ValueReachResult>(`/api/vocab/reach`, body, options),
  },
  client: {
    /**
     * Read the product page → what they sell, who buys, which SIDE to hunt, which LANE, channel fit.
     *
     * Costs: tokens.
     */
    analyzeClient: (body: AnalyzeClientBody, options?: RequestOptions) =>
      http.post<AnalyzeResult>(`/api/client/analyze`, body, options),
    /**
     * Titles, functions, seniority, industries and sizes from the read. Geography is never proposed.
     *
     * Costs: tokens.
     */
    proposeTargeting: (body: ProposeTargetingBody, options?: RequestOptions) =>
      http.post<TargetingProposal>(`/api/client/targeting`, body, options),
  },
  audience: {
    /**
     * A few real members of the pool, reduced to initials and a role. Nothing identifiable.
     *
     * Costs: quota.
     */
    audienceFaces: (body: AudienceFacesBody, options?: RequestOptions) =>
      http.post<AudienceFacesResult>(`/api/audience/faces`, body, options),
  },
  email: {
    /**
     * Whether an address can receive mail: syntax, then the domain's MX records, then the A/AAAA fallback RFC 5321 names. A DNS failure answers checked:false rather than blocking.
     *
     * Costs: free.
     */
    verify: (body: VerifyBody, options?: RequestOptions) =>
      http.post<EmailVerifyResult>(`/api/email/verify`, body, options),
  },
  demo: {
    /**
     * A demo request from the landing. The row is saved before the alert is attempted.
     *
     * Costs: free.
     */
    requestDemo: (body: RequestDemoBody, options?: RequestOptions) =>
      http.post<DemoRequestResult>(`/api/demo`, body, options),
    /**
     * Who asked for a demo, newest first. OPERATOR ONLY — it hands over contact data, so it needs X-Operator-Token.
     *
     * Costs: free.
     */
    listDemoRequests: (query: ListDemoRequestsQuery = {}, options?: RequestOptions) =>
      http.get<DemoRequestList>(withQuery(`/api/demo/requests`, query as Record<string, unknown>), options),
  },
  me: {
    /**
     * Who is signed in (by the Intch Bearer token) and what is saved for them: spec, wizard progress, package. 401 AUTH_REQUIRED without a valid token; 503 AUTH_UPSTREAM when the Intch check did not answer.
     *
     * Costs: free.
     */
    me: (options?: RequestOptions) =>
      http.get<MeResult>(`/api/me`, options),
    /**
     * The signed-in person's purchased searches, the stages of the people in them, and who they have already been given.
     *
     * Costs: free.
     */
    workspace: (options?: RequestOptions) =>
      http.get<AccountWorkspace>(`/api/me/workspace`, options),
    /**
     * Save the spec, the wizard progress, the package, the companies or the campaigns, each whole. A key that is sent is written, null clears it, a missing key is left alone.
     *
     * Costs: free.
     */
    saveState: (body: SaveStateBody, options?: RequestOptions) =>
      http.post<StateSaveResult>(`/api/me/state`, body, options),
    /**
     * Create or update one purchased search. Its run must be yours, or unowned with its real token (it then becomes yours). A repeated order id is not written twice.
     *
     * Costs: free.
     */
    saveSearch: (body: SaveSearchBody, options?: RequestOptions) =>
      http.post<SearchSaveResult>(`/api/me/searches`, body, options),
    /**
     * Replace the stages of the people in every search, whole — the same map the workspace keeps.
     *
     * Costs: free.
     */
    savePicks: (body: SavePicksBody, options?: RequestOptions) =>
      http.post<SavedResult>(`/api/me/picks`, body, options),
    /**
     * Add people to the list of those already delivered. Only ever adds: a person once given is never charged again.
     *
     * Costs: free.
     */
    saveLedger: (body: SaveLedgerBody, options?: RequestOptions) =>
      http.post<LedgerSaveResult>(`/api/me/ledger`, body, options),
    /**
     * Once, after the first sign-in: hand over what the browser kept before accounts existed. A search is accepted only with its real run token; the rest merges without overwriting what the server already has.
     *
     * Costs: free.
     */
    importLocal: (body: ImportLocalBody, options?: RequestOptions) =>
      http.post<ImportResult>(`/api/me/import`, body, options),
  },
  subscribers: {
    /**
     * Persist the buyer contact at onboarding so lifecycle mail can reach them — onboarding otherwise only writes it to localStorage. Public and rate limited; consent timestamps are stored as the client captured them.
     *
     * Costs: free.
     */
    subscribe: (body: SubscribeBody, options?: RequestOptions) =>
      http.post<SubscribeResult>(`/api/subscribe`, body, options),
    /**
     * Every registration with the ad that paid for it and what the person turned out to be — domain, what they sell, which side, which lane. Operator-only; carries no email.
     *
     * Costs: free.
     */
    signups: (query: SignupsQuery = {}, options?: RequestOptions) =>
      http.get<SignupReport>(withQuery(`/api/subscribers/signups`, query as Record<string, unknown>), options),
  },
  lifecycle: {
    /**
     * Run one lifecycle-mail pass: send whatever welcome/nudge is due. Operator-only; also runs on an in-process timer. ?dry=1 reports what would send without sending.
     *
     * Costs: free.
     */
    tick: (body: TickBody, options?: RequestOptions) =>
      http.post<TickResult>(`/api/lifecycle/tick`, body, options),
    /**
     * Drain the SES bounce and complaint queues and suppress the addresses they name. Runs inside every tick too; this is for checking the wiring against the SES simulator. Operator-only.
     *
     * Costs: free.
     */
    suppression: (body: SuppressionBody, options?: RequestOptions) =>
      http.post<DrainResult>(`/api/lifecycle/suppression`, body, options),
    /**
     * The lifecycle send ledger — every welcome/nudge and its outcome. Operator-only (buyer emails).
     *
     * Costs: free.
     */
    sends: (options?: RequestOptions) =>
      http.get<LifecycleSendList>(`/api/lifecycle/sends`, options),
  },
  support: {
    /**
     * One turn of the in-product support chat. Answers from a curated product knowledge base, or says a human is needed. Spends tokens and is rate limited per client.
     *
     * Costs: tokens.
     */
    supportChat: (body: SupportChatBody, options?: RequestOptions) =>
      http.post<SupportChatResult>(`/api/support/chat`, body, options),
    /**
     * Hands the conversation to a person. The row is saved before the alert is attempted, exactly as a demo request is.
     *
     * Costs: free.
     */
    supportHandoff: (body: SupportHandoffBody, options?: RequestOptions) =>
      http.post<SupportHandoffResult>(`/api/support/handoff`, body, options),
  },
  terms: {
    /**
     * Client config → English search phrases. Only the Crux selector consumes them.
     *
     * Costs: tokens.
     */
    deriveTerms: (body: DeriveTermsBody, options?: RequestOptions) =>
      http.post<TermsResult>(`/api/terms`, body, options),
  },
  objects: {
    /**
     * Candidate nouns from the client config, per lane and side, with the vocabulary it dropped.
     *
     * Costs: tokens.
     */
    proposeObjects: (body: ProposeObjectsBody, options?: RequestOptions) =>
      http.post<ProposeObjectsResult>(`/api/objects/propose`, body, options),
    /**
     * Reach per noun through the real lane query (free) plus a five-profile sample per top noun.
     *
     * Costs: quota.
     */
    gradeObjects: (body: GradeObjectsBody, options?: RequestOptions) =>
      http.post<GradeObjectsResult>(`/api/objects/grade`, body, options),
  },
  plan: {
    /**
     * What a run would return, per window. size:0, so it costs nothing.
     *
     * Costs: free.
     */
    planSearch: (body: PlanSearchBody, options?: RequestOptions) =>
      http.post<PlanResult>(`/api/plan`, body, options),
  },
  search: {
    /**
     * Spends quota per returned row. `objects` must carry a matching measurement stamp (client_config.objectsFor === "sells § lane § side") or the route answers 400.
     *
     * Costs: quota.
     */
    runSearch: (body: RunSearchBody, options?: RequestOptions) =>
      http.post<SearchResult>(`/api/search`, body, options),
    /**
     * Find an already-paid run by the key the browser generated before the search, for the case where the answer never arrived. Spends nothing — the run and its quota already exist. Unknown key: 404. POST, not GET, so the key never lands in a proxy log as a URL parameter.
     *
     * Costs: free.
     */
    recoverSearch: (body: RecoverSearchBody, options?: RequestOptions) =>
      http.post<SearchRecoverResult>(`/api/search/recover`, body, options),
    /**
     * The old phrase-floor route, kept as the fallback and as the oracle the equivalence test compares against.
     *
     * Costs: quota.
     */
    runCruxSearch: (body: RunCruxSearchBody, options?: RequestOptions) =>
      http.post<SearchResult>(`/api/search/crux`, body, options),
  },
  core: {
    /**
     * Accumulate a core across pulls. People already held join free; only new people are paid for.
     *
     * Costs: quota.
     */
    growCore: (body: GrowCoreBody, options?: RequestOptions) =>
      http.post<CoreGrowResult>(`/api/core/grow`, body, options),
  },
  phrases: {
    /**
     * How many people each phrase reaches, before anything is spent on it.
     *
     * Costs: free.
     */
    phrasesReach: (body: PhrasesReachBody, options?: RequestOptions) =>
      http.post<PhrasesReachResult>(`/api/phrases/reach`, body, options),
  },
  runs: {
    /**
     * Resolve a signed "back to your shortlist" link from lifecycle mail into the run and its spec, so a person arriving on another device does not restart the wizard. Public but unguessable: the token is an HMAC over the run id. Buyer contact fields are stripped.
     *
     * Costs: free.
     */
    resume: (query: ResumeQuery = {}, options?: RequestOptions) =>
      http.get<ResumeResult>(withQuery(`/api/runs/resume`, query as Record<string, unknown>), options),
    /**
     * What enrichment would cost, per unique domain. Spends nothing.
     *
     * Costs: free.
     */
    runEstimate: (id: string | number, query: RunEstimateQuery = {}, options?: RequestOptions) =>
      http.get<EstimateResult>(withQuery(`/api/runs/${id}/estimate`, query as Record<string, unknown>), options),
    /**
     * Company names → a company identity. Free on the research provider, and cached forever.
     *
     * Costs: free.
     */
    resolveDomains: (id: string | number, body: ResolveDomainsBody, options?: RequestOptions) =>
      http.post<ResolveResult>(`/api/runs/${id}/resolve`, body, options),
    /**
     * Company intelligence per DOMAIN — ten colleagues cost one enrichment. Idempotent.
     *
     * Costs: credits.
     */
    enrichRun: (id: string | number, body: EnrichRunBody, options?: RequestOptions) =>
      http.post<EnrichResult>(`/api/runs/${id}/enrich`, body, options),
    /**
     * Backfill Intch's three timestamps for a run's rows.
     *
     * Costs: quota.
     */
    backfillDates: (id: string | number, body: BackfillDatesBody, options?: RequestOptions) =>
      http.post<DatesResult>(`/api/runs/${id}/dates`, body, options),
    /**
     * The merged rows: the person, their company, the score and the message.
     *
     * Costs: free.
     */
    runRows: (id: string | number, query: RunRowsQuery = {}, options?: RequestOptions) =>
      http.get<RunRowsResult>(withQuery(`/api/runs/${id}/rows`, query as Record<string, unknown>), options),
    /**
     * A public link to this search: the first N people open, without contacts; everyone else behind the paywall. Owner or operator only.
     *
     * Costs: free.
     */
    createShare: (id: string | number, body: CreateShareBody, options?: RequestOptions) =>
      http.post<ShareLink>(`/api/runs/${id}/share`, body, options),
    /**
     * The country tier of a run: how many are in the asked countries, what was relaxed, where to widen.
     *
     * Costs: free.
     */
    runGeo: (id: string | number, options?: RequestOptions) =>
      http.get<RunGeoResult>(`/api/runs/${id}/geo`, options),
    /**
     * The audience the run was priced against: the three band counts stored with the search.
     *
     * Costs: free.
     */
    runPool: (id: string | number, options?: RequestOptions) =>
      http.get<RunPoolResult>(`/api/runs/${id}/pool`, options),
    /**
     * ICP / intent / DM, never merged, with the evidence quoted.
     *
     * Costs: tokens.
     */
    scoreRun: (id: string | number, body: ScoreRunBody, options?: RequestOptions) =>
      http.post<ScoreRunResult>(`/api/runs/${id}/score`, body, options),
    /**
     * First-touch drafts, or refusals naming the rule. Sends nothing. `only` is REQUIRED: drafting a whole queue invites bulk-sending to people the audit would have refused.
     *
     * Costs: tokens.
     */
    draftRun: (id: string | number, body: DraftRunBody, options?: RequestOptions) =>
      http.post<DraftRunResult>(`/api/runs/${id}/draft`, body, options),
    /**
     * The outreach plan for ONE person: the first message (drafted by the touch engine if not yet written) and a deterministic graph of follow-ups and reply branches around it. Owner token or operator. A refusal names its rule.
     *
     * Costs: tokens.
     */
    planRun: (id: string | number, body: PlanRunBody, options?: RequestOptions) =>
      http.post<PlanRunResult>(`/api/runs/${id}/plan`, body, options),
    /**
     * «I sent it»: the person pressed send in the Intch chat and says so. The only clock the sequence can have — follow-ups become dates from this moment. Only on a written message; never moves once set. Owner token or operator.
     *
     * Costs: free.
     */
    markSent: (id: string | number, body: MarkSentBody, options?: RequestOptions) =>
      http.post<SentResult>(`/api/runs/${id}/sent`, body, options),
    /**
     * Let the service send this person's sequence into the Intch chat on the owner's behalf: the first message now, follow-ups on their days, stop on a reply. Only the run's owner, signed in through Intch, with consent: true — neither the operator nor a run token can consent for someone. `texts` are the message texts exactly as the board shows them ({node_id: text}, m1 required): consent is given to those words, and they are frozen as the consent snapshot — only they are ever sent (a follow-up the board did not send takes the plan's text at this moment). The search must be paid (402 PLAN_REQUIRED); 409 ALREADY_REPLIED when the person has replied; 503 AUTOSEND_OFF while sending is off or Intch Connect is not configured; 503 AUTH_UPSTREAM when the sign-in check did not answer.
     *
     * Costs: free.
     */
    startAutosend: (id: string | number, body: StartAutosendBody, options?: RequestOptions) =>
      http.post<AutosendState>(`/api/runs/${id}/autosend`, body, options),
    /**
     * Stop sending this person's sequence. Nothing already sent is recalled. Owner token or operator.
     *
     * Costs: free.
     */
    stopAutosend: (id: string | number, body: StopAutosendBody, options?: RequestOptions) =>
      http.post<AutosendState>(`/api/runs/${id}/autosend/stop`, body, options),
    /**
     * Stop every sequence of this run that is being sent automatically — the list's «Stop automatic sending». Nothing already sent is recalled. Owner token or operator.
     *
     * Costs: free.
     */
    stopAutosendAll: (id: string | number, body: StopAutosendAllBody, options?: RequestOptions) =>
      http.post<AutosendStopAll>(`/api/runs/${id}/autosend/stop-all`, body, options),
    /**
     * Whether this person's sequence is being sent automatically, and what has gone out. Owner token or operator.
     *
     * Costs: free.
     */
    autosendState: (id: string | number, query: AutosendStateQuery = {}, options?: RequestOptions) =>
      http.get<AutosendState>(withQuery(`/api/runs/${id}/autosend`, query as Record<string, unknown>), options),
    /**
     * Rebuild the plan with a hand-set cadence (increasing days 1..30). Free: the first message is reused, and follow-ups already written keep their text. Owner token or operator.
     *
     * Costs: free.
     */
    planCadence: (id: string | number, body: PlanCadenceBody, options?: RequestOptions) =>
      http.post<PlanRunResult>(`/api/runs/${id}/plan/cadence`, body, options),
    /**
     * Rank the whole run for READING ORDER with one cheap model call over slim rows (six fields, ~129 tokens each), so the full scorer reads the most promising people first. Ranks on min(icp, intent). Refuses nobody and filters nothing. Owner token or operator.
     *
     * Costs: tokens.
     */
    prefilterRun: (id: string | number, body: PrefilterRunBody, options?: RequestOptions) =>
      http.post<PrefilterResult>(`/api/runs/${id}/prefilter`, body, options),
    /**
     * Write ONE follow-up or the reply move with the engine, from the same published text (the reply move from the pasted reply). Tokens. Checks are enforced in code; a failed check is a refusal naming it. Owner token or operator. `personal`: «Rewrite from their words» — a follow-up written by the model from the person's own words even where the networking voice assembles reminders from phrases.
     *
     * Costs: tokens.
     */
    planDraft: (id: string | number, body: PlanDraftBody, options?: RequestOptions) =>
      http.post<PlanDraftResult>(`/api/runs/${id}/plan/draft`, body, options),
    /**
     * The buyer's own wording of one message (or the reply move), kept on the server so the card, the board and the copy to chat agree. `reset` returns to the text before the first edit. The first message is closed once marked sent. Free. Owner token or operator.
     *
     * Costs: free.
     */
    planText: (id: string | number, body: PlanTextBody, options?: RequestOptions) =>
      http.post<PlanRunResult>(`/api/runs/${id}/plan/text`, body, options),
    /**
     * Add a follow-up BEFORE the last message (the last note stays last) and write it at once from the person's own words. At most five messages in a sequence; days stay within 1..30 after the first message. Tokens. A refusal changes nothing. Owner token or operator.
     *
     * Costs: tokens.
     */
    planAdd: (id: string | number, body: PlanAddBody, options?: RequestOptions) =>
      http.post<PlanDraftResult>(`/api/runs/${id}/plan/add`, body, options),
    /**
     * Remove one follow-up; the others keep their days and their text. The first message cannot be removed. Free. Owner token or operator.
     *
     * Costs: free.
     */
    planRemove: (id: string | number, body: PlanRemoveBody, options?: RequestOptions) =>
      http.post<PlanRunResult>(`/api/runs/${id}/plan/remove`, body, options),
    /**
     * Store the reply the person sent; the silence branch is over from this moment. Owner token or operator.
     *
     * Costs: free.
     */
    saveReply: (id: string | number, body: SaveReplyBody, options?: RequestOptions) =>
      http.post<ReplyResult>(`/api/runs/${id}/reply`, body, options),
    /**
     * Pull everyone found into this run, in pages, up to target rows (max 2 000) and within the weekly cap of a PAID tier; new rows are queued for scoring on the server. Owner token or operator.
     *
     * Costs: quota.
     */
    extendRun: (id: string | number, body: ExtendRunBody, options?: RequestOptions) =>
      http.post<RunJobsResult>(`/api/runs/${id}/extend`, body, options),
    /**
     * Queue this run's unscored people (or only these) for scoring on the server, so the pass finishes even when the tab is closed. Owner token or operator.
     *
     * Costs: tokens.
     */
    queueScoring: (id: string | number, body: QueueScoringBody, options?: RequestOptions) =>
      http.post<RunJobsResult>(`/api/runs/${id}/score-job`, body, options),
    /**
     * Where the background work on this run is: the pull of everyone found and the scoring queue.
     *
     * Costs: free.
     */
    runJobs: (id: string | number, query: RunJobsQuery = {}, options?: RequestOptions) =>
      http.get<RunJobsResult>(withQuery(`/api/runs/${id}/jobs`, query as Record<string, unknown>), options),
    /**
     * Stop the background work on this run: no new page is pulled and no new batch is claimed.
     *
     * Costs: free.
     */
    cancelRunJobs: (id: string | number, body: CancelRunJobsBody, options?: RequestOptions) =>
      http.post<RunJobsResult>(`/api/runs/${id}/jobs/cancel`, body, options),
    /**
     * How many other members typed the same company string as this lead, and how many of them carry a decision-maker title. Two size:0 counts — free. Owner token or operator.
     *
     * Costs: free.
     */
    companyPeers: (id: string | number, query: CompanyPeersQuery = {}, options?: RequestOptions) =>
      http.get<CompanyPeers>(withQuery(`/api/runs/${id}/company`, query as Record<string, unknown>), options),
    /**
     * Write back scores computed elsewhere.
     *
     * Costs: free.
     */
    saveScores: (id: string | number, body: SaveScoresBody, options?: RequestOptions) =>
      http.post<{ saved: number }>(`/api/runs/${id}/scores`, body, options),
    /**
     * Run history, newest first.
     *
     * Costs: free.
     */
    listRuns: (options?: RequestOptions) =>
      http.get<{ runs: RunListItem[] }>(`/api/runs`, options),
  },
  pay: {
    /**
     * Создать разовый платёж в шлюзе основной платформы и вернуть client_secret для формы оплаты. Прокси на нашей стороне: браузеру незачем знать эндпоинт шлюза, а mock-режим обязан не ходить в сеть.
     *
     * Costs: free.
     */
    createPayment: (body: CreatePaymentBody, options?: RequestOptions) =>
      http.post<PayCreateResult>(`/api/pay/create`, body, options),
    /**
     * The buyer came back from the payment form: set a paid tier on this account for a month, if a payment for this product was started from this same account in the last two days. Not a gateway confirmation — the same trust as /thank-you itself.
     *
     * Costs: free.
     */
    payReturned: (body: PayReturnedBody, options?: RequestOptions) =>
      http.post<{ tier: string; until: string; days: number; run_token?: unknown }>(`/api/pay/returned`, body, options),
    /**
     * This browser's payments confirmed on the server (status paid: the operator checked them in Stripe, later the gateway). The workspace records the ones it does not know yet, so a buyer whose return from the payment form never arrived is not left behind the paywall.
     *
     * Costs: free.
     */
    confirmedPayments: (options?: RequestOptions) =>
      http.get<ConfirmedPayments>(`/api/pay/confirmed`, options),
  },
  replies: {
    /**
     * Intch chat reports that a member replied to a message a buyer sent from Intch. The ONLY event that deducts a reply from a reply-priced plan. Header X-Chat-Key; idempotent on chat_message_id; unmatched deliveries are kept for reconciliation.
     *
     * Costs: free.
     */
    confirmChatReply: (body: ConfirmChatReplyBody, options?: RequestOptions) =>
      http.post<ChatReplyResult>(`/api/replies/confirm`, body, options),
  },
  share: {
    /**
     * What a shared search link shows: the open people (no contacts) and how many stand behind the paywall.
     *
     * Costs: free.
     */
    shareView: (token: string | number, options?: RequestOptions) =>
      http.get<ShareView>(`/api/share/${token}`, options),
  },
  account: {
    /**
     * Localhost only: the Local pill sets this account’s tier, so "everyone found" and the weekly cap can be tried without paying. 403 anywhere else.
     *
     * Costs: free.
     */
    devTier: (body: DevTierBody, options?: RequestOptions) =>
      http.post<GrantTierResult>(`/api/dev/tier`, body, options),
    /**
     * This browser's account (the acct cookie the service sets): tier, the weekly profile cap and what is used of it, replies the Intch chat confirmed, whether background jobs run here.
     *
     * Costs: free.
     */
    getAccount: (options?: RequestOptions) =>
      http.get<AccountResult>(`/api/account`, options),
    /**
     * Operator only: set the tier of the account that owns a run. The only way to a paid tier on the server until the gateway gives a verifiable payment confirmation.
     *
     * Costs: free.
     */
    grantTier: (body: GrantTierBody, options?: RequestOptions) =>
      http.post<GrantTierResult>(`/api/accounts/grant`, body, options),
  },
  obligations: {
    /**
     * Every promise of effort, open ones first.
     *
     * Costs: free.
     */
    listObligations: (options?: RequestOptions) =>
      http.get<{ obligations: Obligation[] }>(`/api/obligations`, options),
    /**
     * Log a "yes, still live" as a promise with a recorded outcome to come.
     *
     * Costs: free.
     */
    addObligation: (body: AddObligationBody, options?: RequestOptions) =>
      http.post<{ id: number }>(`/api/obligations`, body, options),
    /**
     * Close one. An outcome is REQUIRED — that requirement is what makes the promise honest.
     *
     * Costs: free.
     */
    closeObligation: (id: string | number, body: CloseObligationBody, options?: RequestOptions) =>
      http.post<{ closed: boolean }>(`/api/obligations/${id}/close`, body, options),
  },
  crm: {
    /**
     * Everyone in the buyer's searches, once per person, with every search they came from and what happened to them there. Only runs the caller owns (their token or this browser's account); contacts follow the same access as the run's rows. POST so the tokens stay out of the access log.
     *
     * Costs: free.
     */
    listContacts: (body: ListContactsBody, options?: RequestOptions) =>
      http.post<CrmContactsResult>(`/api/crm/contacts`, body, options),
  },
} as const
